import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Forward, PictureAsPdf, Visibility } from '@mui/icons-material';
import {
  ActionPlan,
  createEezyguardClient,
  EezyguardScope,
  EVENT_LABELS,
  forwardToEezycare,
  Note,
  OccurrenceDetail,
  STATUS_LABELS,
  PRIORITY_LABELS,
  EezyguardNoteVisibility,
} from 'src/apis/eezyguard';
import SkeletonLoad from 'src/pages/components/SkeletonLoad';
import { PriorityChip, SeverityChip, StatusChip } from './common';
import { EEZYGUARD_TYPE_LABELS } from './labels';

// Próximos status válidos (espelha a máquina de estados do backend).
const NEXT_STATUSES: Record<string, string[]> = {
  received: ['under_review', 'archived'],
  under_review: ['in_progress', 'unsubstantiated', 'archived'],
  in_progress: ['completed', 'archived'],
  completed: ['archived'],
  unsubstantiated: ['archived'],
  archived: [],
};

type Props = { scope: EezyguardScope };

function Field({ label, value }: { label: string; value?: string | null }): JSX.Element {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  );
}

export default function EezyGuardOccurrenceDetail({ scope }: Props): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const occurrenceId = Number(id);
  const client = useMemo(() => createEezyguardClient(scope), [scope]);
  const isAdmin = scope === 'admin';

  const [detail, setDetail] = useState<OccurrenceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteVisibility, setNoteVisibility] = useState<EezyguardNoteVisibility>(
    isAdmin ? 'shared' : 'company_internal'
  );
  const [planTitle, setPlanTitle] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const openForward = () => {
    setConsentChecked(false);
    setForwardOpen(true);
  };

  const load = useCallback(() => {
    setLoading(true);
    client
      .getOccurrence(occurrenceId)
      .then(setDetail)
      .catch(() => setError('Erro ao carregar a ocorrência.'))
      .finally(() => setLoading(false));
  }, [client, occurrenceId]);

  useEffect(() => {
    load();
  }, [load]);

  const occ = detail?.occurrence;

  const changeStatus = async (status: string) => {
    if (!occ) return;
    let justification: string | undefined;
    if (status === 'archived') {
      justification =
        window.prompt('Justificativa para arquivamento:') || undefined;
      if (!justification) return;
    }
    setSavingStatus(true);
    try {
      await client.patchOccurrence(occ.id, { status, justification });
      load();
    } catch {
      setError('Não foi possível alterar o status (transição inválida?).');
    } finally {
      setSavingStatus(false);
    }
  };

  const changePriority = async (priority: string) => {
    if (!occ) return;
    try {
      await client.patchOccurrence(occ.id, { priority });
      load();
    } catch {
      setError('Não foi possível alterar a prioridade.');
    }
  };

  const addNote = async () => {
    if (!occ || !noteContent.trim()) return;
    try {
      await client.addNote(occ.id, { content: noteContent, visibility: noteVisibility });
      setNoteContent('');
      load();
    } catch {
      setError('Não foi possível adicionar a observação.');
    }
  };

  const addPlan = async () => {
    if (!occ || !planTitle.trim()) return;
    try {
      await client.addActionPlan(occ.id, {
        title: planTitle,
        description: planDescription || undefined,
      } as Partial<ActionPlan>);
      setPlanTitle('');
      setPlanDescription('');
      load();
    } catch {
      setError('Não foi possível criar o plano de ação.');
    }
  };

  const doForward = async () => {
    if (!occ) return;
    setForwarding(true);
    try {
      await forwardToEezycare(occ.id);
      setForwardOpen(false);
      load();
    } catch {
      setError('Não foi possível encaminhar para a Eezycare.');
    } finally {
      setForwarding(false);
    }
  };

  const openEvidence = async (evidenceId: number) => {
    if (!occ) return;
    try {
      const url = await client.getEvidenceUrl(occ.id, evidenceId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Não foi possível abrir a evidência.');
    }
  };

  const downloadPdf = () => {
    const href = `${process.env.REACT_APP_BACKEND_URL ?? ''}${client.pdfUrl(
      occurrenceId
    )}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <SkeletonLoad variant="form" rows={6} />;
  if (!occ) return <Alert severity="error">Ocorrência não encontrada.</Alert>;

  // Empresa-first: quem encaminha (para a EezyCare) é a EMPRESA.
  const canForward = !isAdmin && !occ.forwarded_to_eezycare;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Voltar
        </Button>
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusChip status={occ.status} />
          <SeverityChip severity={occ.severity} />
          <PriorityChip priority={occ.priority} />
          {occ.is_anonymous && <Chip size="small" label="Anônima" variant="outlined" />}
          <Button startIcon={<PictureAsPdf />} size="small" onClick={downloadPdf}>
            PDF
          </Button>
        </Stack>
      </Box>

      <Typography variant="h6">Protocolo {occ.protocol}</Typography>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Dados da ocorrência
          </Typography>
          <Grid container spacing={2}>
            <Field
              label="Tipo"
              value={EEZYGUARD_TYPE_LABELS[occ.occurrence_type] ?? occ.occurrence_type}
            />
            <Field label="Setor" value={occ.sector} />
            <Field label="Local" value={occ.location} />
            <Field label="Complemento" value={occ.location_details} />
            <Field label="Data aproximada" value={occ.approximate_date} />
            <Field
              label="Recebida em"
              value={new Date(occ.created_at).toLocaleString('pt-BR')}
            />
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Relato
          </Typography>
          <Typography variant="body2" whiteSpace="pre-wrap">
            {occ.description || '—'}
          </Typography>
          <Grid container spacing={2} mt={0.5}>
            <Field label="Pessoas envolvidas" value={occ.people_involved} />
            <Field label="Testemunhas" value={occ.witnesses} />
            <Field label="Informações adicionais" value={occ.additional_information} />
          </Grid>
        </CardContent>
      </Card>

      {(occ.reporter_name || occ.reporter_email) && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Identificação do denunciante
            </Typography>
            <Grid container spacing={2}>
              <Field label="Nome" value={occ.reporter_name} />
              <Field label="E-mail" value={occ.reporter_email} />
              <Field label="Telefone" value={occ.reporter_phone} />
              <Field label="Cargo" value={occ.reporter_role} />
              {isAdmin && (
                <Field label="Relação" value={occ.reporter_relationship} />
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Evidências
          </Typography>
          {detail?.evidences.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sem evidências.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {detail?.evidences.map((ev) => (
                <Box
                  key={ev.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2">
                    {ev.original_filename ?? 'arquivo'} — {ev.content_type ?? ''}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => openEvidence(ev.id)}
                  >
                    Ver
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {!isAdmin && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Tratativa
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Alterar status"
                  value=""
                  disabled={savingStatus || NEXT_STATUSES[occ.status].length === 0}
                  onChange={(e) => changeStatus(e.target.value)}
                >
                  {NEXT_STATUSES[occ.status].map((s) => (
                    <MenuItem key={s} value={s}>
                      {STATUS_LABELS[s as keyof typeof STATUS_LABELS]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Prioridade"
                  value={occ.priority ?? ''}
                  onChange={(e) => changePriority(e.target.value)}
                >
                  {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                {canForward ? (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Forward />}
                    onClick={openForward}
                    sx={{ height: '100%' }}
                  >
                    Encaminhar para EezyCare
                  </Button>
                ) : (
                  occ.forwarded_to_eezycare && (
                    <Chip
                      color="success"
                      label="Encaminhada à EezyCare"
                      sx={{ mt: 1 }}
                    />
                  )
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {isAdmin && occ.forwarded_to_eezycare && (
        <Chip color="success" label="Encaminhada pela empresa" />
      )}

      <NotesCard
        notes={detail?.notes ?? []}
        isAdmin={isAdmin}
        content={noteContent}
        setContent={setNoteContent}
        visibility={noteVisibility}
        setVisibility={setNoteVisibility}
        onAdd={addNote}
      />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Plano de ação
          </Typography>
          <Stack spacing={1} mb={2}>
            {(detail?.action_plans ?? []).map((plan) => (
              <Box key={plan.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {plan.title} — {plan.status}
                </Typography>
                {plan.description && (
                  <Typography variant="body2" color="text.secondary">
                    {plan.description}
                  </Typography>
                )}
                {plan.due_date && (
                  <Typography variant="caption">Prazo: {plan.due_date}</Typography>
                )}
              </Box>
            ))}
            {(detail?.action_plans ?? []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Sem plano de ação.
              </Typography>
            )}
          </Stack>
          {!isAdmin && (
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Título"
                  value={planTitle}
                  onChange={(e) => setPlanTitle(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descrição"
                  value={planDescription}
                  onChange={(e) => setPlanDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={addPlan}
                  sx={{ height: '100%' }}
                >
                  Adicionar
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <TimelineCard history={detail?.history ?? []} />

      <Dialog
        open={forwardOpen}
        onClose={() => setForwardOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Encaminhar para a EezyCare</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            A EezyCare passará a visualizar esta ocorrência para apoio/consultoria.
          </Typography>
          <Grid container spacing={1} mt={0.5}>
            <Field
              label="Tipo"
              value={EEZYGUARD_TYPE_LABELS[occ.occurrence_type] ?? occ.occurrence_type}
            />
            <Field label="Gravidade" value={occ.severity} />
            <Field label="Prioridade" value={occ.priority ?? '—'} />
            <Field
              label="Anônima"
              value={occ.is_anonymous ? 'Sim' : 'Não'}
            />
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            {occ.is_anonymous
              ? 'Denúncia anônima: a identidade não é compartilhada com ninguém.'
              : occ.identity_share_authorized
              ? 'A identidade autorizada será visível à EezyCare (mesma regra da empresa).'
              : 'O denunciante não autorizou compartilhar a identidade.'}
          </Alert>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontSize: 13,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2" paragraph>
              Ao confirmar este encaminhamento, a empresa autoriza a Eezycare a acessar
              as informações desta ocorrência exclusivamente para fins de consultoria,
              orientação técnica e apoio na gestão de riscos psicossociais.
            </Typography>
            <Typography variant="body2" paragraph>
              A Eezycare poderá analisar as informações compartilhadas e apresentar
              recomendações, sugestões de encaminhamento e planos de ação.
            </Typography>
            <Typography variant="body2" paragraph>
              A condução da investigação, a validação dos fatos, a adoção de medidas
              disciplinares e as decisões administrativas permanecem sob
              responsabilidade exclusiva da empresa.
            </Typography>
            <Typography variant="body2">
              Ao prosseguir, a empresa declara possuir legitimidade para compartilhar
              essas informações com a Eezycare para essa finalidade.
            </Typography>
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
            }
            label="Li e concordo."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={doForward}
            disabled={forwarding || !consentChecked}
          >
            {forwarding ? 'Encaminhando…' : 'Confirmar encaminhamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function NotesCard({
  notes,
  isAdmin,
  content,
  setContent,
  visibility,
  setVisibility,
  onAdd,
}: {
  notes: Note[];
  isAdmin: boolean;
  content: string;
  setContent: (v: string) => void;
  visibility: EezyguardNoteVisibility;
  setVisibility: (v: EezyguardNoteVisibility) => void;
  onAdd: () => void;
}): JSX.Element {
  const options: { value: EezyguardNoteVisibility; label: string }[] = isAdmin
    ? [
        { value: 'eezycare_internal', label: 'Interna (Eezycare)' },
        { value: 'shared', label: 'Compartilhada' },
      ]
    : [
        { value: 'company_internal', label: 'Interna (empresa)' },
        { value: 'shared', label: 'Compartilhada' },
      ];

  const visibilityLabel = (v: string) =>
    v === 'eezycare_internal'
      ? 'Interna Eezycare'
      : v === 'company_internal'
      ? 'Interna empresa'
      : 'Compartilhada';

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Observações
        </Typography>
        <Stack spacing={1} mb={2}>
          {notes.map((note) => (
            <Box key={note.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Chip size="small" label={visibilityLabel(note.visibility)} sx={{ mb: 0.5 }} />
              <Typography variant="body2" whiteSpace="pre-wrap">
                {note.content}
              </Typography>
            </Box>
          ))}
          {notes.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Sem observações.
            </Typography>
          )}
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label="Nova observação"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
            />
          </Grid>
          <Grid item xs={8} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Visibilidade"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as EezyguardNoteVisibility)}
            >
              {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4} sm={1}>
            <Button fullWidth variant="outlined" onClick={onAdd} sx={{ height: '100%' }}>
              +
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function TimelineCard({
  history,
}: {
  history: OccurrenceDetail['history'];
}): JSX.Element {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Timeline
        </Typography>
        <Stack spacing={1}>
          {history.map((h) => (
            <Box key={h.id} display="flex" gap={1} alignItems="baseline">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>
                {new Date(h.created_at).toLocaleString('pt-BR')}
              </Typography>
              <Typography variant="body2">
                {EVENT_LABELS[h.event_type] ?? h.event_type}
                {h.previous_value && h.new_value
                  ? `: ${h.previous_value} → ${h.new_value}`
                  : ''}
              </Typography>
            </Box>
          ))}
          {history.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Sem histórico.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
